/** 
 Copyright Highway9 Networks Inc. 
 */
import React, { useEffect, useState, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import { history, updateURL } from '~/helpers/history';
import { DISCONNECTED, DEVICE_VIEW } from "../../constants";
import { Types } from "../../constants/types";
import { useInterval } from "../../hooks/useInterval";
import {
  deviceGroupActions,
  deviceGroupEntities,
  deviceGroupSelectedIDs,
  fetchDeviceGroups,
  showSyntheticSimsSelector,
  subscriberDeviceGroups,
} from "../../store/context/deviceGroupSlice";
import {
  fetchDNN,
  fetchDevices,
  fetchDeviceRsrpMetrics,
  fetchDevicesLatestMetrics,
  subscriberActions,
  subscriberEntities,
  subscriberData,
  fetchSubscriberDeviceInfo,
  subscriberPreset,
  subscriberSelected,
  subscriberMetricsClient1,
  subscriberMetricsClient2,
  timelineClient,
} from "../../store/context/subscriberSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Device } from "../../types/device";
import moment from "moment-timezone";
import { setSelectedID, utilityActions } from "~/store/context/utilitySlice";
import { SelectChangeEvent } from "@h9/ui-lib";
import { DEVICES, SUBSCRIBER_TAB_NAMES } from "~/constants/routes";

export const defaultViewHiddenColumns = [
  "deviceGroupNameForTopN",
  "runtimeInfo.apn",
  "runtimeInfo.ipv4",
  "runtimeInfo.macAddress",
  "ueId",
  "uplinkSinr",
  "pDownlinkCqi",
  "pDownlinkMcs",
  "uplinkMcs",
  "p1DownlinkBler",
  "p2DownlinkBler",
  "uplinkBler",
  "downlinkRate",
  "uplinkRate",
  "pathloss",
  "txPower",
  "signalStrength",
  "PHY-DL",
  "RI-Rx-Tx",
  "PHY-UL",
  "rssi"
];
export const topNViewHiddenColumns = [
  "_deviceGroupName",
  "lte.state",
  "status",
  "statusMessage",
  "runtimeInfo.apn",
  "iccid",
  "simState",
  "runtimeInfo.macAddress"
];

export const AIRSPANColumns = [
  "PHY-DL",
  "RI-Rx-Tx",
  "PHY-UL",
  "rssi"
]
export const AIRSPANOnlyColumns = [
  "RI-Rx-Tx",
  "rssi"
]
export const HiddenColumnsForAirspanAndCLASS_M = [
  "downlinkRate",
  "uplinkRate",
  "pathloss",
  "txPower",
]

/**
 * options.passive: consume the page state without owning its lifecycle -
 * skips the mount fetches, the 30s metrics poll, and the shared-worker
 * termination on unmount. Secondary instances (Device Administration dialog)
 * must pass it so only the page instance owns the workers.
 */
export const useDevicePage = (options?: { passive?: boolean }) => {
  const passive = options?.passive ?? false;
  const dispatch = useAppDispatch();
  const subscribers = useAppSelector(subscriberData);
  const devicesById = useAppSelector(subscriberEntities);
  const deviceGroups = useAppSelector(subscriberDeviceGroups);
  const deviceGroupsById = useAppSelector(deviceGroupEntities);
  const selectedDevice = useAppSelector(subscriberSelected);
  const selectedGroupIDS = useAppSelector(deviceGroupSelectedIDs);
  const loadingStatus = useAppSelector((state) => state.subscriber.status);

  const [tabIndex, setTabIndex] = React.useState<string>(SUBSCRIBER_TAB_NAMES.SUMMARY);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const [simTypeFilter, setSimTypeFilter] = useState('ALL');
  const showSyntheticSims = useAppSelector(showSyntheticSimsSelector);

  const preset = useAppSelector(subscriberPreset);

  const [isLoading, setIsLoading] = useState(true);

  const handleSimTypeChange = (event: SelectChangeEvent<string | number | (string | number)[]>) => {
    setSimTypeFilter(String(event.target.value));
  };

  const devices = useMemo(() => {
    const showAllGroups = selectedGroupIDS.includes(Types.all);
    const isTopN = preset === DEVICE_VIEW.TOP_N_VIEW;

    return subscribers.filter((item) => {
      if (!showAllGroups && !selectedGroupIDS.includes(item.groupId!)) {
        return false;
      }
      if (isTopN) {
        const currentState = item?.runtimeInfo?.status ?? DISCONNECTED;
        if (currentState === DISCONNECTED) {
          return false;
        }
      }
      if (simTypeFilter !== 'ALL' && item.simType !== simTypeFilter) {
        return false;
      }
      if (!showSyntheticSims && item.simType.includes("SYNTHETIC")) {
        return false;
      }
      return true;
    });
  }, [subscribers, preset, selectedGroupIDS, simTypeFilter, showSyntheticSims]);

  useEffect(() => {
    if (loadingStatus === "devices_loaded") {
      setIsLoading(false);
    }
  }, [loadingStatus]);

  const getObjectFromIDCallback = (id: string | null) => {
    if (!id) {
      const first = devices[0];
      if (first?.id) {
        dispatch(setSelectedID(first.id));
      }
      return {
        type: Types.device,
        obj: first,
        index: first ? 0 : -1,
      };
    }

    dispatch(setSelectedID(id));
    const type = id.split("-")[0];
    switch (type) {
      case Types.device:
      case Types.endUserDevice:
        return {
          type: Types.device,
          obj: devicesById[id],
          index: devices.findIndex((item) => item.id === id),
        };
      case Types.deviceGroup:
        return {
          type: Types.deviceGroup,
          obj: deviceGroupsById[id],
          index: deviceGroups.findIndex((item) => item.id === id),
        };
      default:
        return undefined;
    }
  };

  const getObjectFromIDRef = useRef(getObjectFromIDCallback);

  useLayoutEffect(() => {
    getObjectFromIDRef.current = getObjectFromIDCallback;
  });

  const getObjectFromID = useCallback((id: string | null) => {
    return getObjectFromIDRef.current(id);
  }, []);

  useEffect(() => {
    function handleSelectionByURL() {
      const params = new URLSearchParams(window.location.search);

      // Apply group filter before device selection so the table row index matches the filtered list
      // (e.g. View Report from site UE report: ?id=<device>&menuID=<group>&tab=ue-report).
      const menuID = params.get("menuID");
      if (menuID) {
        dispatch(deviceGroupActions.setSelectedDeviceGroupIDs([menuID] as string[]));
      }

      const id = params.get("id");
      const selected = getObjectFromID(id);
      if (selected?.obj) {
        switch (selected.type) {
          case Types.device:
            dispatch(subscriberActions.setSelected((selected.obj as Device).id ?? null));
            setSelectedIndex(selected.index);
            break;
          case Types.deviceGroup:
            dispatch(deviceGroupActions.setSelected(selected.obj.id ?? null));
            break;
        }
      }

      const tab = params.get("tab");
      if (tab) {
        setTabIndex(tab);
      }

      const view = params.get("preset");
      if (view) {
        dispatch(subscriberActions.setPreset(view));
        dispatch(
          subscriberActions.setHiddenColumns(view === DEVICE_VIEW.TOP_N_VIEW ? topNViewHiddenColumns : defaultViewHiddenColumns)
        );
      }
    }

    if (!isLoading) {
      handleSelectionByURL();
    }

    const unListen = history.listen(() => {
      // history.listen also fires on leave; only handle while still on the devices page
      if (location.pathname === DEVICES) {
        handleSelectionByURL();
      }
    });

    return () => { unListen(); };

  }, [dispatch, isLoading, getObjectFromID]);

  // After menuID updates the filtered devices list, re-resolve the highlighted row index.
  // handleSelectionByURL can run before selectedGroupIDS has refreshed `devices`.
  useEffect(() => {
    if (isLoading) return;
    const id = new URLSearchParams(window.location.search).get("id");
    if (!id || (id.split("-")[0] !== Types.device && id.split("-")[0] !== Types.endUserDevice)) {
      return;
    }
    const index = devices.findIndex((item) => item.id === id);
    setSelectedIndex(index);
  }, [devices, isLoading]);

  useEffect(() => {
    if (passive) return;
    (async () => {
      // Groups must settle before devices so fetchDevices enrichment sees group entities.
      // DNN / device-info are independent and can run in parallel with groups.
      await Promise.all([
        dispatch(fetchDeviceGroups()),
        dispatch(fetchDNN()),
        dispatch(fetchSubscriberDeviceInfo()),
      ]);
      await dispatch(fetchDevices());
      dispatch(fetchDevicesLatestMetrics());
    })();

    return () => {
      subscriberMetricsClient1.terminate();
      subscriberMetricsClient2.terminate();
      timelineClient.terminate();
      setIsLoading(true);
    };
  }, [dispatch, passive]);

  useInterval(async () => { dispatch(fetchDevicesLatestMetrics()); }, passive ? null : 1000 * 30);

  useEffect(() => {
    if (!selectedDevice?.id) return;
    dispatch(fetchDeviceRsrpMetrics([selectedDevice.id]));
  }, [dispatch, selectedDevice?.id]);

  useIntervalSetter("3hour", tabIndex);

  function handlePresetChange(event: SelectChangeEvent<string | number | (string | number)[]>) {
    const value = String(event.target.value);
    dispatch(subscriberActions.setPreset(value));
    dispatch(
      subscriberActions.setHiddenColumns(
        value === DEVICE_VIEW.TOP_N_VIEW ? topNViewHiddenColumns : defaultViewHiddenColumns,
      ),
    );
    updateURL({
      preset: value,
    });
  }

  return {
    isLoading,
    data: subscribers,
    deviceGroups,
    preset,
    devices,
    selectedDevice,
    tabIndex,
    setTabIndex,
    selectedIndex,
    setSelectedIndex,
    handlePresetChange,
    handleSimTypeChange,
    simTypeFilter
  };
};

/**
 * Caps a wide time range when switching device tabs so timeline/metrics loads stay responsive.
 * If start→end spans more than `difference` days, resets the utility time interval.
 */
function useIntervalSetter(interval: "3hour", tab: string, difference = 2) {
  const dispatch = useAppDispatch();
  const startTimeI = useAppSelector((state) => state.utility.time.startTime);
  const endTimeI = useAppSelector((state) => state.utility.time.endTime);

  // biome-ignore lint/correctness/useExhaustiveDependencies: on startTimeI, endTimeI change
  useEffect(() => {
    if (moment.unix(endTimeI).diff(moment.unix(startTimeI), "days") > difference) {
      dispatch(utilityActions.setTimeInterval({ interval, shouldUpdateTime: true }));
    }
  }, [difference, dispatch, interval, tab]);
}
